import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ElectronService } from '@educational-initiatives/angular-web-utilities';
import { ScreenProtectionService } from '@educational-initiatives/angular-web-utilities';
import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';
import { NetworkBannerComponent } from '@app/shared/components/network-banner/network-banner.component';
import { PosthogService } from '@app/core/services/posthog/posthog.service';

/**
 * @fileoverview AppComponent
 * @description
 * **The Orchestrator**
 * The root of the application hierarchy, responsible for initializing global services
 * and providing the primary routing outlet. It acts as the anchor point for the
 * entire component tree.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Root-level orchestration
 * 2. Global service initialization
 * 3. Reactive state synchronization
 */
@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, NetworkBannerComponent, ErrorModalComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _electronService = inject(ElectronService);
  private readonly _screenProtectionService = inject(ScreenProtectionService);

  constructor() {
    inject(PosthogService);
  }

  /**
   * @description
   * **App Initialization**
   * Initializes the application by setting up the Electron environment and screen protection.
   *
   * TECHNICAL RATIONALE: Required to bridge the gap between web and desktop contexts,
   * and to initialize security features like screen protection.
   * TECHNICAL DESCRIPTION: Invokes the initialization sequence in the Electron service
   * and the screen protection service.
   * TECHNICAL PURPOSE: Prepares the desktop-specific features, IPC bridge, and screen
   * protection mechanisms.
   *
   * JUNIOR DEV NARRATIVE:
   * "When the app starts, we need to tell Electron that we're ready to go and
   * activate screen protection features."
   */
  public ngOnInit(): void {
    this._electronService.initialize();
    this._screenProtectionService.init();
  }

  /**
   * @description
   * **App Cleanup**
   * Cleans up application resources before destruction.
   *
   * TECHNICAL RATIONALE: Prevents memory leaks and ensures clean termination of desktop processes.
   * TECHNICAL DESCRIPTION: Invokes the cleanup sequence in the Electron service.
   * TECHNICAL PURPOSE: Releases Electron resources and closes IPC channels.
   *
   * JUNIOR DEV NARRATIVE:
   * "Before the app closes, we need to tidy up everything we started."
   */
  public ngOnDestroy(): void {
    this._electronService.cleanup();
  }
}
